import type { SavedCalculation } from '@/types/cvr';
import type { FatigueSavePayload } from '@/types/fatigue';
import { supabase } from './client';
import { logError } from '@/utils/logger';

export async function fetchFatigueCalculations(): Promise<SavedCalculation[]> {
  const { data, error } = await supabase
    .from('fatigue_calculations')
    .select('*')
    .order('date_saved', { ascending: false });

  if (error) {
    logError('Error fetching fatigue calculations:', error);
    throw new Error('Failed to fetch calculations');
  }

  return data;
}

export async function saveFatigueCalculation(
  projectName: string,
  data: FatigueSavePayload
): Promise<SavedCalculation> {
  if (!projectName?.trim()) {
    throw new Error('Name is required');
  }

  if (!data || typeof data !== 'object') {
    throw new Error('Invalid calculation data');
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User must be authenticated to save calculations');
  }

  const { data: savedData, error } = await supabase
    .from('fatigue_calculations')
    .insert({
      project_name: projectName.trim(),
      data,
      user_id: user.id,
    })
    .select()
    .single();

  if (error) {
    logError('Error saving fatigue calculation:', error);
    throw new Error('Failed to save calculation');
  }

  return savedData;
}

export async function renameFatigueCalculation(
  id: string,
  newName: string
): Promise<void> {
  const { error } = await supabase
    .from('fatigue_calculations')
    .update({ project_name: newName })
    .eq('id', id);

  if (error) {
    logError('Error renaming fatigue calculation:', error);
    throw new Error('Failed to rename calculation');
  }
}

export async function deleteFatigueCalculation(id: string): Promise<void> {
  const { error } = await supabase
    .from('fatigue_calculations')
    .delete()
    .eq('id', id);

  if (error) {
    logError('Error deleting fatigue calculation:', error);
    throw new Error('Failed to delete calculation');
  }
}
